/**
 * Notes -> IrNote (W1-04 core-ir; rpe-format.md F§11, DESIGN.md §6.12, §7.2).
 *
 *  - order: (t, x, id), as the document index keeps them (Phira sorts notes itself; RPE writes them by time);
 *  - type: tap 1, hold 2, flick 3, drag 4;
 *  - startTime / endTime: raw imported triples while equal in value; a non-hold note's endTime is its startTime,
 *    or `NoteSrc.nonHoldEnd` while the note is unedited (Phira reads every note's endTime for max_time);
 *  - above 1 / 2 and isFake 0 / 1, or the preserved raw integers while they mean the same;
 *  - visibleTime null -> 999999.0 (RPE's "always");
 *  - hitsound: built-in sounds as "tap.mp3" / "flick.mp3" / "drag.mp3", assets as their package path;
 *  - tint, tintHitEffects, judgeArea only when set; unknown keys from `NoteSrc.extra`.
 */

import { RPE_VISIBLE_ALWAYS } from '../model/defaults';
import type { Hitsound, LineId, Note, NoteKind } from '../model/types';
import type { RGB } from '../util/types';
import type { LowerCtx } from './lowerCtx';
import { tripleOut } from './triples';
import type { IrNote } from './types';

/** RPE note type of a note kind. */
export const NOTE_TYPE: Readonly<Record<NoteKind, IrNote['type']>> = Object.freeze({
  tap: 1,
  hold: 2,
  flick: 3,
  drag: 4,
});

/** File name Phira maps to each built-in hitsound. */
export const BUILTIN_HITSOUND_FILE: Readonly<Record<'tap' | 'drag' | 'flick', string>> = Object.freeze({
  tap: 'tap.mp3',
  drag: 'drag.mp3',
  flick: 'flick.mp3',
});

function clampU8(v: number): number {
  const r = Math.round(v);
  return Number.isNaN(r) ? 0 : r < 0 ? 0 : r > 255 ? 255 : r;
}

/** Colours are clamped explicitly to integer channels 0..255 (WORKPACKAGES rule 8). */
export function rgbOut(c: RGB): RGB {
  return [clampU8(c[0]), clampU8(c[1]), clampU8(c[2])];
}

function hitsoundOut(ctx: LowerCtx, h: Hitsound): string {
  return h.kind === 'builtin' ? BUILTIN_HITSOUND_FILE[h.sound] : ctx.assetPath(h.asset);
}

/** One note. */
export function lowerNote(ctx: LowerCtx, n: Note): IrNote {
  const src = n.src;
  const startTime = tripleOut(n.t, src?.rawStart, ctx.acc);
  let endTime;
  if (n.kind === 'hold') endTime = tripleOut(n.end, src?.rawEnd, ctx.acc);
  else endTime = src?.nonHoldEnd ?? startTime;
  const aboveRaw = src?.aboveRaw;
  const above = n.above ? 1 : aboveRaw !== undefined && aboveRaw !== 1 ? aboveRaw : 2;
  const fakeRaw = src?.isFakeRaw;
  const isFake = n.fake ? (fakeRaw !== undefined && fakeRaw !== 0 ? fakeRaw : 1) : 0;
  const out: { -readonly [K in keyof IrNote]: IrNote[K] } = {
    type: NOTE_TYPE[n.kind],
    startTime,
    endTime,
    positionX: n.x,
    above,
    isFake,
    speed: n.speed,
    yOffset: n.yOffset,
    size: n.size,
    alpha: n.alpha,
    visibleTime: n.visibleTime ?? RPE_VISIBLE_ALWAYS,
    extra: src?.extra ?? [],
    srcNote: n.id,
  };
  if (n.hitsound !== null) out.hitsound = hitsoundOut(ctx, n.hitsound);
  if (n.tint !== null) out.tint = rgbOut(n.tint);
  if (n.tintHitEffects !== null) out.tintHitEffects = rgbOut(n.tintHitEffects);
  if (n.judgeArea !== null) out.judgeArea = n.judgeArea;
  return out;
}

/** Notes of a line, sorted by (t, x, id). */
export function lowerNotes(ctx: LowerCtx, line: LineId): IrNote[] {
  const out: IrNote[] = [];
  for (const id of ctx.idx.notesByLine(line)) {
    const n = ctx.doc.notes.get(id);
    if (n !== undefined) out.push(lowerNote(ctx, n));
  }
  return out;
}

/** RPE `numOfNotes` (re-derived on export): the line's real (non-fake) notes, as RPE counts them. */
export function countRealNotes(ctx: LowerCtx, line: LineId): number {
  let count = 0;
  for (const id of ctx.idx.notesByLine(line)) if (ctx.doc.notes.get(id)?.fake === false) count++;
  return count;
}
